import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ReactComponent as CloseIcon } from "assets/close.svg";
import { toast } from "react-toastify";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import VideoCameraBackIcon from "@mui/icons-material/VideoCameraBack";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import { getRmsConfig } from "features/config/configSlice";
import parse from "html-react-parser";
import {
  areOptionalReturnMediaConfigsCollectivelyMissing,
  getMediaRequirementMaximum,
  getMediaRequirementMinimum,
  getMediaRequirementKey,
  getReasonMinimumMediaUploads,
  getReturnMediaFileValidationError,
  isReturnMediaUploadForConfig,
} from "utils/returnMedia";

const CAPTURE_INSTRUCTIONS = {
  front_view: "Show the complete front of the product clearly.",
  side_view: "Show the complete side of the product clearly.",
  back_view: "Show the complete back of the product clearly.",
  packaging: "Show the product packaging clearly.",
  brand_tag: "Show the original brand tag clearly.",
  size_tag: "Show the size tag and its details clearly.",
  defect: "Clearly show the damaged or faulty area.",
  other: "Upload a clear view of the product issue.",
};

const getMaximum = (configs, fallback = 0) =>
  configs.length
    ? configs.reduce(
        (total, config) => total + getMediaRequirementMaximum(config),
        0,
      )
    : Number(fallback || 0);

function ImageUpload(props) {
  const {
    uploads = [],
    setUploads,
    preview = [],
    setPreview,
    selectedReason,
    uploadMediaIds,
    setUploadMediaIds,
    setImageUploadCount,
    setVideoUploadCount,
    lineItemId,
    validationAttempted,
  } = props;

  const [slowUpload, setSlowUpload] = useState(false);
  const [activeImagePreview, setActiveImagePreview] = useState(null);
  const rmsConfig = useSelector(getRmsConfig);
  const mediaConfigs = useMemo(
    () =>
      Array.isArray(selectedReason?.media_configs)
        ? selectedReason.media_configs
            .map((config, index) => ({
              ...config,
              upload_key: getMediaRequirementKey(config, index),
            }))
            .sort(
              (first, second) =>
                Number(first.sort_order || 0) - Number(second.sort_order || 0),
            )
        : [],
    [selectedReason],
  );
  const imageConfigs = mediaConfigs.filter(
    (config) => config.media_type === "image",
  );
  const videoConfigs = mediaConfigs.filter(
    (config) => config.media_type === "video",
  );
  const hasStructuredConfigs = mediaConfigs.length > 0;
  const minimumPhotos = getReasonMinimumMediaUploads(selectedReason, "image");
  const minimumVideos = getReasonMinimumMediaUploads(selectedReason, "video");
  const maximumPhotos = hasStructuredConfigs
    ? getMaximum(imageConfigs)
    : Math.max(minimumPhotos, Number(selectedReason?.max_number_of_photos || 5));
  const maximumVideos = hasStructuredConfigs
    ? getMaximum(videoConfigs)
    : Math.max(minimumVideos, Number(selectedReason?.max_number_of_videos || 3));
  const displayMediaConfigs = mediaConfigs.length
    ? mediaConfigs
    : [
        minimumPhotos > 0
          ? {
              id: "legacy-image",
              media_type: "image",
              capture_code: "other",
              capture_label: "Product photos",
              customer_instruction: "Upload clear photos of the product.",
              required: true,
              min_count: minimumPhotos,
              max_count: maximumPhotos,
              sort_order: 0,
            }
          : null,
        minimumVideos > 0
          ? {
              id: "legacy-video",
              media_type: "video",
              capture_code: "other",
              capture_label: "Product videos",
              customer_instruction: "Upload clear videos of the product.",
              required: true,
              min_count: minimumVideos,
              max_count: maximumVideos,
              sort_order: 1,
            }
          : null,
      ].filter(Boolean);
  const optionalConfigsCollectivelyMissing =
    areOptionalReturnMediaConfigsCollectivelyMissing(
      mediaConfigs,
      uploads,
    );

  const handleSubmit = (event, requestedMediaType, mediaConfig = null) => {
    const selectedFiles = Array.from(event.target.files || []);
    const filesToUpload = [];
    let hasLargeFile = false;
    let imagesCount = 0;
    let videosCount = 0;
    const currentTypeCount = uploads.filter((file) => {
      if (mediaConfig) {
        return isReturnMediaUploadForConfig(file, mediaConfig);
      }
      return file?.type?.startsWith(`${requestedMediaType}/`);
    }).length;
    const maximumCount = mediaConfig
      ? getMediaRequirementMaximum(mediaConfig)
      : requestedMediaType === "image"
        ? maximumPhotos
        : maximumVideos;

    selectedFiles.forEach((file) => {
      const fileSizeMB = file.size / 1000000;
      const validationError = getReturnMediaFileValidationError(
        file,
        requestedMediaType,
      );

      if (validationError) {
        toast.error(validationError);
        return;
      }
      if (maximumCount > 0 && currentTypeCount + filesToUpload.length >= maximumCount) {
        toast.error(
          `You can upload a maximum of ${maximumCount} ${
            requestedMediaType === "image" ? "photos" : "videos"
          }.`,
        );
        return;
      }

      hasLargeFile = hasLargeFile || fileSizeMB > 5;
      imagesCount += requestedMediaType === "image" ? 1 : 0;
      videosCount += requestedMediaType === "video" ? 1 : 0;
      if (mediaConfig) {
        file.media_config_id =
          mediaConfig.upload_key || getMediaRequirementKey(mediaConfig);
        file.capture_code = mediaConfig.capture_code;
        file.capture_label = mediaConfig.capture_label;
      }
      filesToUpload.push(file);
    });

    if (!filesToUpload.length) {
      event.target.value = "";
      return;
    }

    setImageUploadCount((previous) => previous + imagesCount);
    setVideoUploadCount((previous) => previous + videosCount);
    setUploads([...uploads, ...filesToUpload]);
    setPreview([
      ...preview,
      ...filesToUpload.map((file) => URL.createObjectURL(file)),
    ]);
    setSlowUpload(hasLargeFile);
    event.target.value = "";
  };

  const removeMedia = (index) => {
    if (preview[index] && String(preview[index]).startsWith("blob:")) {
      URL.revokeObjectURL(preview[index]);
    }
    const updatedUploads = uploads.filter((_, itemIndex) => itemIndex !== index);
    const updatedPreviews = preview.filter((_, itemIndex) => itemIndex !== index);
    const updatedUploadMediaIds = (uploadMediaIds || []).map((entry) =>
      String(entry.line_item_id) === String(lineItemId)
        ? {
            ...entry,
            uploads: (entry.uploads || []).filter(
              (_, itemIndex) => itemIndex !== index,
            ),
          }
        : entry,
    );

    setImageUploadCount(
      updatedUploads.filter((file) => file?.type?.startsWith("image/")).length,
    );
    setVideoUploadCount(
      updatedUploads.filter((file) => file?.type?.startsWith("video/")).length,
    );
    setUploads(updatedUploads);
    setPreview(updatedPreviews);
    setUploadMediaIds(updatedUploadMediaIds);
  };

  return (
    <div className="media-upload">
      {displayMediaConfigs.length > 0 ? (
        <div className="media-upload-guidelines">
          <p className="media-upload-guidelines-title">Upload media</p>
          <div className="media-upload-guideline-list">
            {displayMediaConfigs.map((config) => {
              const configUploads = uploads
                .map((file, index) => ({ file, index, previewUrl: preview[index] }))
                .filter(({ file }) =>
                  isReturnMediaUploadForConfig(file, config),
                );
              const isImage = config.media_type === "image";
              const configKey = config.upload_key || config.id;
              const inputId = `capture-${configKey}-${lineItemId}`;
              const minimum = getMediaRequirementMinimum(config);
              const maximum = getMediaRequirementMaximum(config);
              const isRequired = Boolean(config.required || minimum > 0);
              const isCollectivelyMissing =
                optionalConfigsCollectivelyMissing && !isRequired;
              const isMissing =
                validationAttempted &&
                ((isRequired && configUploads.length < minimum) ||
                  isCollectivelyMissing);

              return (
                <div
                  className={`media-upload-guideline ${
                    isImage ? "is-image" : "is-video"
                  } ${configUploads.length ? "has-preview" : ""} ${
                    configUploads.length >= minimum && minimum > 0
                      ? "is-complete"
                      : ""
                  } ${isMissing ? "has-error" : ""}`}
                  key={configKey || config.capture_code}
                >
                  <div className="media-upload-card-media">
                    <div className="media-upload-guideline-previews">
                      {configUploads.map(({ file, index, previewUrl }) => (
                        <div className="media-upload-guideline-preview" key={`${file.name}-${index}`}>
                          {isImage ? (
                            <img
                              src={previewUrl}
                              alt={config.capture_label || "Uploaded media"}
                              role="button"
                              tabIndex={0}
                              onClick={() => setActiveImagePreview(previewUrl)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  setActiveImagePreview(previewUrl);
                                }
                              }}
                            />
                          ) : (
                            <video src={previewUrl} controls muted preload="metadata" />
                          )}
                          <button
                            type="button"
                            onClick={() => removeMedia(index)}
                            aria-label="Remove uploaded media"
                          >
                            <CloseIcon />
                          </button>
                        </div>
                      ))}
                      {maximum === 0 || configUploads.length < maximum ? (
                        <>
                          <input
                            id={inputId}
                            type="file"
                            className="media-upload-input"
                            accept={isImage ? "image/png,image/jpg,image/jpeg" : "video/mp4"}
                            multiple={maximum > 1}
                            onChange={(event) =>
                              handleSubmit(event, config.media_type, config)
                            }
                          />
                          <label className="media-upload-guideline-add" htmlFor={inputId}>
                            {isImage ? (
                              <AddPhotoAlternateOutlinedIcon />
                            ) : (
                              <VideoCameraBackIcon />
                            )}
                          </label>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className="media-upload-card-copy">
                    <p className="media-upload-guideline-label">
                      {config.capture_label || config.capture_code}
                    </p>
                    <p className="media-upload-guideline-instruction">
                      {config.customer_instruction ||
                        CAPTURE_INSTRUCTIONS[config.capture_code] ||
                        CAPTURE_INSTRUCTIONS.other}
                    </p>
                    {isMissing ? (
                      <p className="media-upload-guideline-error">
                        {isCollectivelyMissing
                          ? "Please upload at least one file from any of these media options."
                          : `Please upload at least ${minimum} ${
                              isImage ? "photo" : "video"
                            }${minimum === 1 ? "" : "s"}.`}
                      </p>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="media-upload-tips">
            <TipsAndUpdatesOutlinedIcon />
            <span>
              Please add photos and videos before creating a request, or may be
              auto-declined by systems
            </span>
          </div>
        </div>
      ) : null}

      {activeImagePreview ? (
        <div
          className="media-image-preview-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={() => setActiveImagePreview(null)}
        >
          <div className="media-image-preview-dialog">
            <img
              src={activeImagePreview}
              alt="Full size uploaded preview"
              onClick={(event) => event.stopPropagation()}
            />
            <button
              type="button"
              onClick={() => setActiveImagePreview(null)}
              aria-label="Close image preview"
            >
              <CloseIcon />
            </button>
          </div>
        </div>
      ) : null}

      {slowUpload ? (
        <p className="media-upload-slow-note">
          <ErrorOutlineIcon />
          Files larger than 5MB may take a little longer to upload.
        </p>
      ) : null}
      {rmsConfig.brand_instruction_for_images &&
      !rmsConfig.brand_instruction_for_images
        .toLowerCase()
        .includes("please add photos and videos before creating a request") ? (
        <div className="line-item-image-instructions">
          {parse(rmsConfig.brand_instruction_for_images)}
        </div>
      ) : null}
    </div>
  );
}

export default ImageUpload;
